<template>
  <crud-modal
    :show="show"
    :title="operation === 'add' ? 'Nuevo Usuario' : 'Editar Usuario'"
    size="lg"
    :loading="loading"
    save-text="Guardar"
    @save="guardarUsuario"
    @close="$emit('close')"
  >
    <!-- Nav tabs -->
    <ul class="nav nav-tabs mb-3" role="tablist">
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'datos' }"
          href="#"
          @click.prevent="activeTab = 'datos'"
        >
          <i class="bi bi-person-fill mr-1"></i> Datos de Usuario
        </a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: activeTab === 'permisos' }"
          href="#"
          @click.prevent="activeTab = 'permisos'"
        >
          <i class="bi bi-shield-lock-fill mr-1"></i> Permisos de Acceso
          <span
            v-if="form.permisosSeleccionados && form.permisosSeleccionados.length"
            class="badge badge-primary ml-1"
          >
            {{ form.permisosSeleccionados.length }}
          </span>
        </a>
      </li>
    </ul>

    <!-- Pestaña 1: Datos de Usuario -->
    <div v-show="activeTab === 'datos'">
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="font-weight-bold small text-muted mb-1">Nombre de Usuario *</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors && errors.errors && errors.errors.name }"
            placeholder="Ej: jdoe"
            v-model="form.name"
            :readonly="operation === 'edit'"
          />
          <div v-if="errors && errors.errors && errors.errors.name" class="invalid-feedback">
            <div v-for="(err, idx) of errors.errors.name" :key="idx">{{ err }}</div>
          </div>
          <small class="form-text text-muted">
            Nombre de usuario para inicio de sesión (alfanumérico sin espacios).
          </small>
        </div>

        <div class="form-group col-md-6">
          <label class="font-weight-bold small text-muted mb-1">Correo Electrónico</label>
          <input
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors && errors.errors && errors.errors.email }"
            placeholder="usuario@ejemplo.com"
            v-model="form.email"
          />
          <div v-if="errors && errors.errors && errors.errors.email" class="invalid-feedback">
            <div v-for="(err, idx) of errors.errors.email" :key="idx">{{ err }}</div>
          </div>
          <small class="form-text text-muted">
            Dirección de correo electrónico válida para notificaciones.
          </small>
        </div>
      </div>

      <!-- Contraseña (sólo en modo alta) -->
      <div v-if="operation === 'add'" class="form-row">
        <div class="form-group col-md-6">
          <label class="font-weight-bold small text-muted mb-1">Contraseña *</label>
          <div class="input-group">
            <input
              class="form-control"
              :class="{
                'is-invalid': errors && errors.errors && errors.errors.password,
                'is-valid': passwordStrength && passwordStrength.isValid
              }"
              :type="passwordFieldType"
              placeholder="Mínimo 8 caracteres"
              v-model="form.password"
              @input="checkPasswordStrength"
            />
            <div class="input-group-append">
              <button
                class="btn btn-outline-secondary"
                type="button"
                @click="passwordFieldType = passwordFieldType === 'password' ? 'text' : 'password'"
              >
                <i :class="passwordFieldType === 'password' ? 'bi-eye-fill' : 'bi-eye-slash-fill'"></i>
              </button>
            </div>
            <div v-if="errors && errors.errors && errors.errors.password" class="invalid-feedback">
              <div v-for="(err, idx) of errors.errors.password" :key="idx">{{ err }}</div>
            </div>
          </div>
          <small class="form-text text-muted">Mínimo 8 caracteres, números y letras.</small>
          <div v-if="passwordStrength" class="mt-1 small font-weight-bold">
            <span :class="passwordStrength.isValid ? 'text-success' : 'text-danger'">
              {{ passwordStrength.message }}
            </span>
          </div>
        </div>

        <div class="form-group col-md-6">
          <label class="font-weight-bold small text-muted mb-1">Repetir Contraseña *</label>
          <div class="input-group">
            <input
              class="form-control"
              :class="{
                'is-invalid': (errors && errors.errors && errors.errors.password_confirmation) || (form.password_confirmation && form.password !== form.password_confirmation)
              }"
              :type="passwordFieldType"
              placeholder="Repita la contraseña"
              v-model="form.password_confirmation"
            />
            <div v-if="errors && errors.errors && errors.errors.password_confirmation" class="invalid-feedback">
              <div v-for="(err, idx) of errors.errors.password_confirmation" :key="idx">{{ err }}</div>
            </div>
            <div v-else-if="form.password_confirmation && form.password !== form.password_confirmation" class="invalid-feedback d-block">
              Las contraseñas no coinciden.
            </div>
          </div>
          <small class="form-text text-muted">Confirme la misma contraseña.</small>
        </div>
      </div>

      <!-- Selección de Integradores Asociados -->
      <div class="form-group mb-3">
        <label class="font-weight-bold small text-muted mb-1">
          Integradores Asociados
          <span class="badge badge-light border text-muted ml-1">
            {{ (form.id_integradores || []).length }} seleccionado(s)
          </span>
        </label>
        <div class="input-group input-group-sm mb-2">
          <input
            type="text"
            class="form-control"
            placeholder="Filtrar integradores..."
            v-model="filtroTextoIntegrador"
          />
          <div class="input-group-append" v-if="filtroTextoIntegrador">
            <button class="btn btn-outline-secondary" type="button" @click="filtroTextoIntegrador = ''">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
        <div
          class="border rounded p-2 bg-white"
          style="max-height: 180px; overflow-y: auto; overflow-x: hidden;"
        >
          <div
            class="form-check mb-1"
            v-for="integ in integradoresFiltrados"
            :key="integ.id_integrador"
          >
            <input
              type="checkbox"
              class="form-check-input"
              :id="'integ_' + integ.id_integrador"
              :value="integ.id_integrador"
              v-model="form.id_integradores"
            />
            <label
              class="form-check-label small"
              :for="'integ_' + integ.id_integrador"
              style="cursor: pointer;"
            >
              {{ integ.integrador }}
              <small class="text-muted">(ID: {{ integ.id_integrador }})</small>
              <small v-if="integ.activo == 0" class="text-muted">(inactivo)</small>
            </label>
          </div>
          <div
            v-if="!integradoresFiltrados.length"
            class="text-muted small py-1"
          >
            {{
              todosIntegradores && todosIntegradores.length
                ? "No hay integradores que coincidan con la búsqueda."
                : "No hay integradores disponibles."
            }}
          </div>
        </div>
        <small class="form-text text-muted">
          Marque los integradores sobre los cuales este usuario tendrá visibilidad de datos.
        </small>
      </div>
    </div>

    <!-- Pestaña 2: Permisos de Acceso -->
    <div v-show="activeTab === 'permisos'">
      <div class="form-group mb-3">
        <label for="form_rol_select" class="font-weight-bold small text-muted mb-1">Rol del Usuario</label>
        <select
          class="form-control"
          v-model="form.rol"
          @change="onRolChange"
          id="form_rol_select"
        >
          <option v-for="r in roles" :key="r.id_rol" :value="r.nombre">
            {{ r.descripcion || r.nombre }}
          </option>
        </select>
        <small class="form-text text-muted">
          Al cambiar el rol se preseleccionan los permisos permitidos para dicho rol. Puede afinar permisos específicos a continuación:
        </small>
      </div>

      <usuario-permisos-tree
        v-model="form.permisosSeleccionados"
        :arbol-permisos="computedArbolPermisos"
      />
    </div>

    <div v-if="generalError" class="alert alert-danger mt-3 mb-0 small">
      <i class="bi bi-exclamation-triangle-fill mr-1"></i> {{ generalError }}
    </div>
  </crud-modal>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import CrudModal from "../utiles/CrudModal.vue";
import UsuarioPermisosTree from "./UsuarioPermisosTree.vue";

export default {
  name: "UsuarioFormModal",
  components: {
    CrudModal,
    UsuarioPermisosTree,
  },
  props: {
    show: { type: Boolean, default: false },
    operation: { type: String, default: "add" }, // 'add' | 'edit'
    user: { type: Object, default: () => ({}) },
    roles: { type: Array, default: () => [] },
    todosIntegradores: { type: Array, default: () => [] },
    arbolPermisos: { type: Array, default: () => [] },
    permisos: { type: Array, default: () => [] },
    rolPermisos: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeTab: "datos",
      form: {
        name: "",
        email: "",
        password: "",
        password_confirmation: "",
        rol: "publica",
        enabled: 1,
        id_integradores: [],
        permisosSeleccionados: [],
      },
      passwordFieldType: "password",
      passwordStrength: null,
      filtroTextoIntegrador: "",
      loading: false,
      errors: null,
      generalError: null,
    };
  },
  computed: {
    integradoresFiltrados() {
      const list = [...this.todosIntegradores];
      if (!this.filtroTextoIntegrador || !this.filtroTextoIntegrador.trim()) {
        return list;
      }
      const term = this.filtroTextoIntegrador.trim().toLowerCase();
      return list.filter((item) => {
        const id = String(item.id_integrador || "");
        const nombre = String(item.integrador || "").toLowerCase();
        return id.includes(term) || nombre.includes(term);
      });
    },

    computedArbolPermisos() {
      if (this.arbolPermisos && this.arbolPermisos.length && !this.form.rol) {
        return this.arbolPermisos;
      }
      const appsMap = {};
      const currentRol = this.form.rol;

      let roleId = null;
      let roleName = null;
      if (currentRol) {
        const cleanRol = String(currentRol).trim().toLowerCase();
        roleName = cleanRol;
        if (this.roles && this.roles.length) {
          const foundRole = this.roles.find(
            (r) =>
              String(r.nombre).toLowerCase() === cleanRol ||
              String(r.id_rol) === cleanRol ||
              (r.descripcion && String(r.descripcion).toLowerCase() === cleanRol)
          );
          if (foundRole) {
            roleId = String(foundRole.id_rol);
            roleName = String(foundRole.nombre).toLowerCase();
          }
        }
      }

      let sourceList = [];
      if (this.rolPermisos && this.rolPermisos.length > 0) {
        sourceList = this.rolPermisos
          .filter((rp) => {
            if (!currentRol) return true;
            const rpName = String(rp.rol_nombre || "").trim().toLowerCase();
            const rpId = String(rp.id_rol || "");
            if (roleId && rpId === roleId) return true;
            if (roleName && rpName === roleName) return true;
            const cleanRol = String(currentRol).trim().toLowerCase();
            if (rpName === cleanRol || rpId === cleanRol) return true;
            return false;
          })
          .map((rp) => ({
            id_permiso: rp.id_permiso,
            nombre: rp.permiso_nombre || rp.nombre,
            descripcion: rp.permiso_descripcion || rp.descripcion || rp.permiso_nombre || rp.nombre,
            aplicacion: rp.aplicacion || this.getAplicacionDePermiso(rp),
          }));
      } else if (this.permisos && this.permisos.length > 0 && (!currentRol || roleName === "admin")) {
        sourceList = this.permisos;
      }

      sourceList.forEach((p) => {
        const nom = p.nombre || p.permiso_nombre;
        if (!nom) return;
        const app = this.getAplicacionDePermiso(p);
        if (!appsMap[app]) {
          appsMap[app] = {
            nombre: app,
            permisos: [],
          };
        }
        if (!appsMap[app].permisos.some((x) => x.nombre === nom)) {
          appsMap[app].permisos.push({
            id_permiso: p.id_permiso,
            nombre: nom,
            descripcion: p.descripcion || p.permiso_descripcion || nom,
            aplicacion: app,
          });
        }
      });

      const appKeys = Object.keys(appsMap).sort();
      return appKeys.map((appKey) => {
        const appObj = appsMap[appKey];
        appObj.permisos.sort((a, b) => a.nombre.localeCompare(b.nombre));
        return appObj;
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.activeTab = "datos";
        this.errors = null;
        this.generalError = null;
        this.passwordStrength = null;
        this.filtroTextoIntegrador = "";
        this.initForm();
      }
    },
    rolPermisos() {
      if (this.show && this.operation === "add") {
        const allowed = this.getPermisosDeRol(this.form.rol);
        if (allowed && allowed.length && (!this.form.permisosSeleccionados || !this.form.permisosSeleccionados.length)) {
          this.form.permisosSeleccionados = [...allowed];
        }
      }
    },
  },
  methods: {
    initForm() {
      if (this.operation === "add") {
        this.form = {
          name: "",
          email: "",
          password: "",
          password_confirmation: "",
          rol: "publica",
          enabled: 1,
          id_integradores: [],
          permisosSeleccionados: [],
        };
        this.onRolChange();
      } else {
        const u = this.user || {};
        let integradoresSel = [];
        if (Array.isArray(u.integradores)) {
          integradoresSel = u.integradores.map((i) => i.id_integrador).filter(Boolean);
        } else if (u.id_integrador && Number(u.id_integrador)) {
          integradoresSel = [Number(u.id_integrador)];
        }

        this.form = {
          id: u.id_usuario || u.id,
          id_usuario: u.id_usuario || u.id,
          name: u.name || "",
          email: u.email || "",
          password: "",
          password_confirmation: "",
          rol: this.normalizeRol(u.rol || u.id_rol),
          enabled: u.enabled !== undefined ? Number(u.enabled) : 1,
          id_integradores: integradoresSel,
          permisosSeleccionados: this.buildPermisosSeleccionados(u),
        };
      }
    },

    normalizeRol(rolVal) {
      if (!rolVal) return "publica";
      const clean = String(rolVal).trim().toLowerCase();
      if (this.roles && this.roles.length) {
        const found = this.roles.find(
          (r) =>
            String(r.nombre).toLowerCase() === clean ||
            String(r.id_rol) === clean ||
            (r.descripcion && String(r.descripcion).toLowerCase() === clean)
        );
        if (found) return found.nombre;
      }
      return clean;
    },

    getPermisosDeRol(rolVal) {
      if (!rolVal) return [];
      const clean = String(rolVal).trim().toLowerCase();
      let roleId = null;
      let roleName = clean;

      if (this.roles && this.roles.length) {
        const found = this.roles.find(
          (r) =>
            String(r.nombre).toLowerCase() === clean ||
            String(r.id_rol) === clean ||
            (r.descripcion && String(r.descripcion).toLowerCase() === clean)
        );
        if (found) {
          roleId = String(found.id_rol);
          roleName = String(found.nombre).toLowerCase();
        }
      }

      const perms = [];
      if (this.rolPermisos && this.rolPermisos.length > 0) {
        this.rolPermisos.forEach((rp) => {
          const rName = String(rp.rol_nombre || "").trim().toLowerCase();
          const rId = String(rp.id_rol || "");
          if (
            (roleId && rId === roleId) ||
            (roleName && rName === roleName) ||
            rName === clean ||
            rId === clean
          ) {
            const pNom = rp.permiso_nombre || rp.nombre;
            if (pNom && !perms.includes(pNom)) perms.push(pNom);
          }
        });
      }
      if (!perms.length && (clean === "admin" || roleName === "admin") && this.permisos) {
        return this.permisos.map((p) => p.nombre || p.permiso_nombre).filter(Boolean);
      }
      return perms;
    },

    buildPermisosSeleccionados(u) {
      let selected = [];
      let permissions = u.permissions || u.permisos;
      if (permissions && typeof permissions === "string") {
        try {
          permissions = JSON.parse(permissions);
        } catch (e) {
          permissions = null;
        }
      }
      if (Array.isArray(permissions)) {
        permissions.forEach((n) => {
          if (n) selected.push(String(n).trim().toUpperCase());
        });
      } else if (permissions && typeof permissions === "object") {
        Object.keys(permissions).forEach((g) => {
          if (g === "admin") return;
          if (Array.isArray(permissions[g])) {
            permissions[g].forEach((n) => {
              if (n) selected.push(String(n).trim().toUpperCase());
            });
          }
        });
      }

      const roleVal = u.rol || u.id_rol;
      const allowed = this.getPermisosDeRol(roleVal);

      if (allowed && allowed.length) {
        selected = selected.filter((p) => allowed.includes(p));
      }

      return [...new Set(selected)];
    },

    onRolChange() {
      const allowed = this.getPermisosDeRol(this.form.rol);
      if (this.operation === "add") {
        this.form.permisosSeleccionados = [...allowed];
      } else {
        this.form.permisosSeleccionados = (this.form.permisosSeleccionados || []).filter((p) =>
          allowed.includes(p)
        );
      }
    },

    checkPasswordStrength() {
      const pwd = this.form.password;
      const minLength = 8;
      const hasUpperCase = /[A-Z]/.test(pwd);
      const hasLowerCase = /[a-z]/.test(pwd);
      const hasNumbers = /[0-9]/.test(pwd);
      const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

      const isValid = pwd.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChars;
      let message = "";
      if (!pwd) message = "La contraseña es obligatoria.";
      else if (!hasUpperCase) message = "Debe contener al menos una mayúscula.";
      else if (!hasLowerCase) message = "Debe contener al menos una minúscula.";
      else if (!hasNumbers) message = "Debe contener al menos un número.";
      else if (!hasSpecialChars) message = "Debe contener un carácter especial.";
      else if (pwd.length < minLength) message = `Mínimo ${minLength} caracteres.`;
      else message = "Contraseña segura";

      this.passwordStrength = { isValid, message };
    },

    getPermissionsJson() {
      const selected = this.form.permisosSeleccionados || [];
      const permissions = {};
      const permisosMap = {};
      (this.permisos || []).forEach((p) => {
        const nom = String(p.nombre || p.permiso_nombre || "").toUpperCase();
        const app = String(p.aplicacion || (nom.startsWith("API_") ? "api" : "genesis")).toLowerCase();
        permisosMap[nom] = app;
      });

      selected.forEach((name) => {
        if (!name) return;
        const upper = String(name).trim().toUpperCase();
        const app = permisosMap[upper] || (upper.startsWith("API_") ? "api" : "genesis");
        if (!permissions[app]) permissions[app] = [];
        if (!permissions[app].includes(upper)) permissions[app].push(upper);
      });

      return JSON.stringify(permissions);
    },

    guardarUsuario() {
      this.errors = null;
      this.generalError = null;

      if (!this.form.name) {
        this.generalError = "El nombre de usuario es obligatorio.";
        this.activeTab = "datos";
        return;
      }
      if (this.operation === "add") {
        if (!this.form.password) {
          this.generalError = "La contraseña es obligatoria para nuevos usuarios.";
          this.activeTab = "datos";
          return;
        }
        if (this.form.password !== this.form.password_confirmation) {
          this.generalError = "Las contraseñas no coinciden.";
          this.activeTab = "datos";
          return;
        }
      }

      this.loading = true;
      const payload = Object.assign({}, this.form, {
        permissions: this.getPermissionsJson(),
        id_integrador: null,
      });

      const endpoint = this.operation === "add" ? "usuario/new" : "usuario/update";
      axios
        .post(process.env.VUE_APP_API_URL + endpoint, payload)
        .then((response) => {
          if (response && response.data && response.data.errno && response.data.errno > 0) {
            this.errors = response.data;
          } else {
            Swal.fire({
              title: "¡Éxito!",
              text: this.operation === "add" ? "Usuario creado correctamente" : "Usuario actualizado correctamente",
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
            });
            this.$emit("saved");
            this.$emit("close");
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.generalError = typeof e.response.data.error === "string"
              ? e.response.data.error
              : (e.response.data.error.message || "Error al guardar usuario");
            if (e.response.data.errors) {
              this.errors = e.response.data;
            }
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getAplicacionDePermiso(p) {
      if (p && p.aplicacion && String(p.aplicacion).trim()) {
        return String(p.aplicacion).trim().toUpperCase();
      }
      const nombre = String(
        (p && (p.permiso_nombre || p.nombre)) || ""
      ).trim().toUpperCase();
      if (nombre.startsWith("API_")) return "API";
      if (nombre.startsWith("GEN_")) return "GENESIS";
      return nombre.length >= 3 ? nombre.substring(0, 3) : "GENERAL";
    },
  },
};
</script>
